import { Reducer, Action } from 'redux';
import { fork, call, takeEvery, put, all, select, delay, putResolve } from 'redux-saga/effects';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';
import LeaseClauseModel from 'src/models/LeaseClause';
import Api from '../api';
import ContractAddendumClause from 'src/components/project/sections/LeasingComponents/redesign/ContractGenerationModels/ContractAddendumClause';
import ErrorResponse from 'src/models/ErrorResponse';
import IndexReferenceType from 'src/models/IndexReferenceType';
import LandContractType from 'src/models/LandContractType';
import AgreementProvisionDto from 'src/models/AgreementProvisionDto';

// tslint:disable no-implicit-any

// Store
export interface ILeaseClauseState {
    clauses: Array<LeaseClauseModel>;
    contractClauses: Array<ContractAddendumClause>;
    provisionClauseTypes: Array<AgreementProvisionDto>;
    provisionValues: Array<IndexReferenceType>;
    provisionUnits: Array<IndexReferenceType>;
    error: string;
    errorCode: string;
}

const initialState: ILeaseClauseState = {
    clauses: undefined,
    contractClauses: undefined,
    provisionClauseTypes: undefined,
    provisionValues: undefined,
    provisionUnits: undefined,
    error: undefined,
    errorCode: undefined
};

// Action Types
export enum actionTypes {
    DELETE_LEASE_CLAUSE = '[clause] DELETE_LEASE_CLAUSE',
    DELETE_ENTITY_ERROR = '[clause] DELETE_ENTITY_ERROR',
    DELETE_PROVISION_CLAUSE_TYPE = '[clause] DELETE_PROVISION_CLAUSE_TYPE',
    DELETE_PROVISION_CLAUSE_TYPE_SUCCESS = '[clause] DELETE_PROVISION_CLAUSE_TYPE_SUCCESS',
    GET_CONTRACT_CLAUSES = '[clause] GET_CONTRACT_CLAUSES',
    GET_CONTRACT_CLAUSES_SUCCESS = '[clause] GET_CONTRACT_CLAUSES_SUCCESS',
    GET_PROVISION_CLAUSE_TYPES = '[clause] GET_PROVISION_CLAUSE_TYPES',
    GET_PROVISION_CLAUSE_TYPES_SUCCESS = '[clause] GET_PROVISION_CLAUSE_TYPES_SUCCESS',
    GET_PROVISION_VALUES = '[clause] GET_PROVISION_VALUES',
    GET_PROVISION_VALUES_SUCCESS = '[clause] GET_PROVISION_VALUES_SUCCESS',
    GET_PROVISION_UNITS = '[clause] GET_PROVISION_UNITS',
    GET_PROVISION_UNITS_SUCCESS = '[clause] GET_PROVISION_UNITS_SUCCESS',
    CREATE_CONTRACT_CLAUSE = '[clause] CREATE_CONTRACT_CLAUSE',
    CREATE_CONTRACT_CLAUSE_SUCCESS = '[clause] CREATE_CONTRACT_CLAUSE_SUCCESS',
    UPDATE_CONTRACT_CLAUSE = '[clause] UPDATE_CONTRACT_CLAUSE',
    CLEAR_CLAUSES = '[clause] CLEAR_CLAUSES',
    CLEAR_ERROR = '[clause] CLEAR_ERROR'
}

// Action Creator
export const actionCreators = {
    deleteLeaseClause: (clientNumber: number, projectId: number, id: number, tractType: string) => {
        return { type: actionTypes.DELETE_LEASE_CLAUSE, payload: { clientNumber, projectId, id, tractType } };
    },
    deleteEntityError: (payload: ErrorResponse) => {
        return { type: actionTypes.DELETE_ENTITY_ERROR, payload: payload };
    },
    deleteProvisionClauseType: (clientNumber: number, projectId: number, id: number, contractType: LandContractType) => {
        return { type: actionTypes.DELETE_PROVISION_CLAUSE_TYPE, payload: { clientNumber, projectId, id, contractType } };
    },
    getContractClauses: (clientNumber: number, projectId: number) => {
        return { type: actionTypes.GET_CONTRACT_CLAUSES, payload: { clientNumber, projectId } };
    },
    getContractClausesSuccess: (clauses: Array<LeaseClauseModel>) => {
        return { type: actionTypes.GET_CONTRACT_CLAUSES_SUCCESS, payload: clauses };
    },
    getProvisionClauseTypes: (clientNumber: number, projectId: number) => {
        return { type: actionTypes.GET_PROVISION_CLAUSE_TYPES, payload: { clientNumber, projectId } };
    },
    getProvisionClauseTypesSuccess: (clauses: Array<AgreementProvisionDto>) => {
        return { type: actionTypes.GET_PROVISION_CLAUSE_TYPES_SUCCESS, payload: clauses };
    },
    getProvisionValues: (clientNumber: number, projectId: number) => {
        return { type: actionTypes.GET_PROVISION_VALUES, payload: { clientNumber, projectId } };
    },
    getProvisionValuesSuccess: (values: Array<IndexReferenceType>) => {
        return { type: actionTypes.GET_PROVISION_VALUES_SUCCESS, payload: values };
    },
    getProvisionUnits: (clientNumber: number, projectId: number) => {
        return { type: actionTypes.GET_PROVISION_UNITS, payload: { clientNumber, projectId } };
    },
    getProvisionUnitsSuccess: (units: Array<IndexReferenceType>) => {
        return { type: actionTypes.GET_PROVISION_UNITS_SUCCESS, payload: units };
    },
    createContractClause: (clientNumber: number, projectId: number, clause: ContractAddendumClause) => {
        return { type: actionTypes.CREATE_CONTRACT_CLAUSE, payload: { clientNumber, projectId, clause } };
    },
    createContractClauseSuccess: () => {
        return { type: actionTypes.CREATE_CONTRACT_CLAUSE_SUCCESS };
    },
    updateContractClause: (clientNumber: number, projectId: number, clause: ContractAddendumClause) => {
        return { type: actionTypes.UPDATE_CONTRACT_CLAUSE, payload: { clientNumber, projectId, clause } };
    },
    clearClauses: () => {
        return { type: actionTypes.CLEAR_CLAUSES };
    },
    clearError: () => {
        return { type: actionTypes.CLEAR_ERROR };
    }
};

// Reducer
const reducer: Reducer<ILeaseClauseState> = (state: ILeaseClauseState = initialState, action: Action): ILeaseClauseState => {
    switch (action.type) {
        case actionTypes.GET_CONTRACT_CLAUSES_SUCCESS: {
            const getContractClausesPayload = (action as { type: string; payload: Array<ContractAddendumClause> }).payload;
            return {
                ...state,
                contractClauses: getContractClausesPayload,
            };
        }
        case actionTypes.GET_PROVISION_CLAUSE_TYPES_SUCCESS: {
            const getProvisionClauseTypesPayload = (action as { type: string; payload: Array<AgreementProvisionDto> }).payload;
            return {
                ...state,
                provisionClauseTypes: getProvisionClauseTypesPayload,
            };
        }
        case actionTypes.GET_PROVISION_VALUES_SUCCESS: {
            const getProvisionClauseTypesPayload = (action as { type: string; payload: Array<IndexReferenceType> }).payload;
            return {
                ...state,
                provisionValues: getProvisionClauseTypesPayload,
            };
        }
        case actionTypes.GET_PROVISION_UNITS_SUCCESS: {
            const getProvisionClauseTypesPayload = (action as { type: string; payload: Array<IndexReferenceType> }).payload;
            return {
                ...state,
                provisionUnits: getProvisionClauseTypesPayload,
            };
        }
        case actionTypes.DELETE_ENTITY_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
                errorCode: payload.messageCode
            };
        }
        case actionTypes.CLEAR_ERROR: {
            return {
                ...state,
                error: undefined,
                errorCode: undefined
            };
        }
        default:
            return state;
    }
};

export default reducer;

// Sagas
const sagas = {
    deleteLeaseClause: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; id: number; tractType: string };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.deleteContractClause, action.payload.clientNumber, action.payload.projectId, action.payload.id, jwt), delay(200)]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.deleteEntityError(result));
        } else {
            yield putResolve(actionCreators.getContractClauses(action.payload.clientNumber, action.payload.projectId));
        }
    },

    deleteProvisionClause: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; id: number; contractType: LandContractType };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.deleteProvisionClauseType, action.payload.clientNumber, action.payload.projectId, action.payload.id, jwt), delay(200)]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.deleteEntityError(result));
        } else {
            yield putResolve(actionCreators.getProvisionClauseTypes(action.payload.clientNumber, action.payload.projectId ));
        }
    },

    getContractClauses: function* (action: { type: string; payload: { clientNumber: number; projectId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.getContractClauses, action.payload.clientNumber, action.payload.projectId, jwt), delay(200)]);
        yield put(actionCreators.getContractClausesSuccess(result));
    },

    getProvisionClauseTypes: function* (action: { type: string; payload: { clientNumber: number; projectId: number; } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.getProvisionClauseTypes, action.payload.clientNumber, action.payload.projectId, jwt), delay(200)]);
        yield put(actionCreators.getProvisionClauseTypesSuccess(result));
    },

    getProvisionValues: function* (action: { type: string; payload: { clientNumber: number; projectId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.getProvisionValues, action.payload.clientNumber, action.payload.projectId, jwt), delay(200)]);
        yield put(actionCreators.getProvisionValuesSuccess(result));
    },

    getProvisionUnits: function* (action: { type: string; payload: { clientNumber: number; projectId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.getProvisionUnits, action.payload.clientNumber, action.payload.projectId, jwt), delay(200)]);
        yield put(actionCreators.getProvisionUnitsSuccess(result));
    },

    createContractClause: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; clause: ContractAddendumClause };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.Clauses.createContractClause, action.payload.clientNumber, action.payload.projectId, action.payload.clause, jwt);
        yield putResolve(actionCreators.getContractClauses(action.payload.clientNumber, action.payload.projectId));    },

    updateContractClause: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; clause: ContractAddendumClause };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.Clauses.updateContractClause, action.payload.clientNumber, action.payload.projectId, action.payload.clause, jwt);
        yield putResolve(actionCreators.getContractClauses(action.payload.clientNumber, action.payload.projectId));    },
};

// Watches
const watches = {
    deleteLeaseClause: function* () {
        yield takeEvery(actionTypes.DELETE_LEASE_CLAUSE, sagas.deleteLeaseClause);
    },
    deleteProvisionClause: function* () {
        yield takeEvery(actionTypes.DELETE_PROVISION_CLAUSE_TYPE, sagas.deleteProvisionClause);
    },
    getContractClauses: function* () {
        yield takeEvery(actionTypes.GET_CONTRACT_CLAUSES, sagas.getContractClauses);
    },
    getProvisionClauseTypes: function* () {
        yield takeEvery(actionTypes.GET_PROVISION_CLAUSE_TYPES, sagas.getProvisionClauseTypes);
    },
    getProvisionValues: function* () {
        yield takeEvery(actionTypes.GET_PROVISION_VALUES, sagas.getProvisionValues);
    },
    getProvisionUnits: function* () {
        yield takeEvery(actionTypes.GET_PROVISION_UNITS, sagas.getProvisionUnits);
    },
    createContractClause: function* () {
        yield takeEvery(actionTypes.CREATE_CONTRACT_CLAUSE, sagas.createContractClause);
    },
    updateContractClause: function* () {
        yield takeEvery(actionTypes.UPDATE_CONTRACT_CLAUSE, sagas.updateContractClause);
    },
};

// Forks
export function* saga() {
    yield fork(watches.deleteLeaseClause);
    yield fork(watches.deleteProvisionClause);
    yield fork(watches.getContractClauses);
    yield fork(watches.getProvisionClauseTypes);
    yield fork(watches.getProvisionValues);
    yield fork(watches.getProvisionUnits);
    yield fork(watches.createContractClause);
    yield fork(watches.updateContractClause);
}



// WEBPACK FOOTER //
// ./src/reducers/Clause.ts